import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
} from "react";
import classNames from "classnames";
import styles from "./styles.module.scss";

interface SliderProps {
  min?: number;
  max?: number;
  step?: number;
  value: number;
  onChange: (value: number) => void;
  className?: string;
}

const Slider: React.FC<SliderProps> = ({
                                         min = 0,
                                         max = 100,
                                         step = 1,
                                         value,
                                         onChange,
                                         className,
                                       }) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);

  const calcValueFromPos = useCallback(
    (clientX: number) => {
      if (!trackRef.current) return value;
      const { left, width } = trackRef.current.getBoundingClientRect();
      let pos = clientX - left;
      pos = Math.max(0, Math.min(width, pos));
      const ratio = pos / width;
      let newValue = min + ratio * (max - min);
      newValue = Math.round(newValue / step) * step;
      return Math.max(min, Math.min(max, newValue));
    },
    [min, max, step, value]
  );

  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      if (!dragging) return;
      onChange(calcValueFromPos(e.clientX));
    },
    [dragging, calcValueFromPos, onChange]
  );

  const handlePointerUp = useCallback(() => {
    setDragging(false);
  }, []);

  useEffect(() => {
    if (dragging) {
      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", handlePointerUp);
      window.addEventListener("pointercancel", handlePointerUp);
      return () => {
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerup", handlePointerUp);
        window.removeEventListener("pointercancel", handlePointerUp);
      };
    }
  }, [dragging, handlePointerMove, handlePointerUp]);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      setDragging(true);
      onChange(calcValueFromPos(e.clientX));
      e.preventDefault();
    },
    [calcValueFromPos, onChange]
  );

  const ratio = (value - min) / (max - min);
  const thumbLeft = ratio * 100;

  return (
    <div
      className={classNames(
        styles.slider,
        { [styles.dragging]: dragging },
        className
      )}
      ref={trackRef}
      onPointerDown={handlePointerDown}
      role="slider"
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={value}
      tabIndex={0}
      onKeyDown={(e) => {
        let next = value;
        if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
          next = Math.max(min, value - step);
          onChange(next);
          e.preventDefault();
        } else if (e.key === "ArrowRight" || e.key === "ArrowUp") {
          next = Math.min(max, value + step);
          onChange(next);
          e.preventDefault();
        }
      }}
    >
      <div className={styles.track} />
      <div
        className={styles.filled}
        style={{ width: `${thumbLeft}%` }}
      />
      <div
        className={styles.thumb}
        style={{ left: `${thumbLeft}%` }}
      />
    </div>
  );
};

export default Slider;